// workspacePresets.js — THE preset data layer of the Workspace Presets program
// (wt/presets/CONTRACT.md §5): the registry read, the questions every surface
// asks of it, and the ONE apply. The App Store "Workspace presets" row and its
// sheet (this package) and the new-account wizard (packages/workspace) call
// these BY NAME:
//
//   el.call('workspacePresetsLoad', force)      → Promise<preset[]>
//   el.call('workspacePresetsFeatured', root)    → featured presets, registry order
//   el.call('workspacePresetByKey', key, root)   → preset | null
//   el.call('workspacePresetApplied', root)      → the active workspace's
//                                                  settings.preset | null
//   el.call('workspacePresetCanApply', root)     → owner/admin of the active workspace
//   el.call('workspacePresetApply', key, opts)   → Promise<result> — opts
//                                                  { include, exclude, setup }
//   el.call('workspacePresetText', key, field)   → the store's name / line /
//                                                  summary of a preset ('' when
//                                                  this surface has none)
//
// WHERE THEY ARE REGISTERED. The shell registers this file at BOOT through
// marketplace/functions/eagerSlice.js (workspace/context.js merges that slice),
// so the wizard reaches every name before any /marketplace route folds the
// store in. The store's own barrel (functions/index.js) exports it too: the
// standalone store and the lazy fold read it there. The fold is add-missing,
// so the shell never holds two copies. The comm host registers no store
// function and runs no wizard, so it does not carry these.
//
// ROOT STATE — declared in packages/workspace/state-app.js (classified in
// state.js / state-app.js for the workspace switch) and in this package's
// state.js for the standalone store:
//   _workspacePresets            null until the first good read, then preset[]
//                                (GET /core/presets, the v2 wire)
//   _workspacePresetsAt          ms of the last good read; 0 = never
//   _workspacePresetsUnsupported true when this host's SDK has no
//                                `listWorkspacePresets` (an older SDK pin): every
//                                surface hides its preset UI — a capability
//                                check, not an error
//   _workspacePresetsError       null | 'failed' — the last read failed; the list
//                                keeps its last good value
//   _presetApply                 null | { key, workspaceId, status:
//                                'running'|'done'|'error', lines: [{ appId,
//                                state: 'pending'|'installed'|'skipped'|'failed',
//                                error }], result, error, cause? } — `cause`
//                                ('offline' | 'server') only on a failed call
//                                whose cause is known
//
// BACKEND: SDK only (`sdk.listWorkspacePresets()`, `sdk.applyWorkspacePreset
// (workspaceId, key, { include, exclude, setup })`) — never a raw fetch.
//
// FRANK: every export is self-contained (FA204) — no module-scope helper or
// constant is read from inside one; siblings and shared helpers are reached
// through `this.call`, the receiver `el.call` hands every function.

// el.call('workspacePresetsLoad', force) — read the preset registry into
// root._workspacePresets. A held list younger than five minutes (the server
// caches the route for 300 s) answers without a request unless `force`. One
// read runs at a time per app: the root ELEMENT's scope holds the in-flight
// promise (per-instance, non-reactive storage — never module state). Resolves
// the list — the last good one when a read fails — and never rejects.
export async function workspacePresetsLoad(force) {
  const el = this
  const root = (el && el.state && el.state.root) || null
  if (!root || typeof root.update !== 'function') return []
  const held = Array.isArray(root._workspacePresets) ? root._workspacePresets : null
  const heldAt = Number(root._workspacePresetsAt) || 0
  if (!force) {
    if (root._workspacePresetsUnsupported === true) return []
    if (held && Date.now() - heldAt < 300000) return held
  }
  const sdk =
    (el && el.sdk) ||
    (el && el.context && el.context.methods && el.context.methods.sdk) ||
    (el && typeof el.call === 'function' ? el.call('getSDK') : null) ||
    null
  if (!sdk || sdk.isNullClient === true || typeof sdk.listWorkspacePresets !== 'function') {
    if (root._workspacePresetsUnsupported !== true) {
      root.update({ _workspacePresetsUnsupported: true })
    }
    return []
  }
  const rootEl = el && typeof el.getRoot === 'function' ? el.getRoot() : null
  const bag = rootEl && rootEl.scope && typeof rootEl.scope === 'object' ? rootEl.scope : null
  if (bag && bag.workspacePresetsInFlight) return bag.workspacePresetsInFlight
  // The v2 wire, copied into plain rows: app ids lower-cased (the matching
  // rule every catalog read uses), the setup lists filtered to their shape.
  const toPreset = (p) => {
    const ids = (list) =>
      (Array.isArray(list) ? list : [])
        .map((id) =>
          String(id || '')
            .trim()
            .toLowerCase()
        )
        .filter((id) => id.indexOf('/') > 0)
    const setup = p.setup && typeof p.setup === 'object' ? p.setup : {}
    const channels = (Array.isArray(setup.channels) ? setup.channels : [])
      .filter((c) => c && typeof c.name === 'string' && c.name.trim())
      .map((c) => ({
        name: c.name.trim(),
        topic: typeof c.topic === 'string' ? c.topic : '',
        // P1: the channel's words per language ({ en, ka }) — the apply seeds
        // the workspace-language one, so the sheet names that one too
        words: c.words && typeof c.words === 'object' ? c.words : null
      }))
    const folders = (Array.isArray(setup.folders) ? setup.folders : [])
      .filter((f) => typeof f === 'string' && f.trim())
      .map((f) => f.trim())
    const folderWords = Array.isArray(setup.folderWords) ? setup.folderWords : []
    // The preset's own line for each of its apps, per language
    // (`{ '<owner>/<key>': { en, ka } }`): the welcome's Your apps reads it
    // before the store's generic line. Ids lower-cased like `apps`; only
    // string lines kept.
    const appWords = {}
    const said = setup.appWords && typeof setup.appWords === 'object' ? setup.appWords : {}
    for (const raw of Array.isArray(said) ? [] : Object.keys(said)) {
      const id = String(raw || '')
        .trim()
        .toLowerCase()
      const per = said[raw]
      if (id.indexOf('/') <= 0 || !per || typeof per !== 'object') continue
      const words = {}
      for (const lang of Object.keys(per)) {
        if (typeof per[lang] === 'string' && per[lang].trim()) words[lang] = per[lang].trim()
      }
      if (Object.keys(words).length) appWords[id] = words
    }
    // The apps' industry words (server `setup.crm` / `setup.scheduling`): an
    // app reads them here when its workspace holds no copy (the CRM's
    // crmPresetWords.js, Scheduling's schPreset.js). Plain copies; a block
    // that is not an object stays out.
    const industryWords = {}
    for (const k of ['crm', 'scheduling']) {
      const b = setup[k]
      if (b && typeof b === 'object' && !Array.isArray(b))
        industryWords[k] = JSON.parse(JSON.stringify(b))
    }
    const order = Number(p.order)
    return {
      key: p.key,
      apps: ids(p.apps),
      suggested: ids(p.suggested),
      setup: { channels, folders, folderWords, appWords, ...industryWords },
      featured: p.featured === true,
      order: Number.isFinite(order) ? order : 999
    }
  }
  const run = (async () => {
    try {
      const res = await sdk.listWorkspacePresets()
      const rows = Array.isArray(res) ? res : res && Array.isArray(res.presets) ? res.presets : null
      if (!rows) throw new Error('the preset registry answered no list')
      const presets = rows
        .filter((p) => p && typeof p.key === 'string' && p.key && Array.isArray(p.apps))
        .map(toPreset)
      root.update({
        _workspacePresets: presets,
        _workspacePresetsAt: Date.now(),
        _workspacePresetsUnsupported: false,
        _workspacePresetsError: null
      })
      return presets
    } catch (err) {
      console.warn('[workspacePresets] the registry read failed:', (err && err.message) || err)
      root.update({ _workspacePresetsError: 'failed' })
      return held || []
    } finally {
      if (bag) bag.workspacePresetsInFlight = null
    }
  })()
  if (bag) bag.workspacePresetsInFlight = run
  return run
}

// el.call('workspacePresetsFeatured', root) — the featured presets in registry
// `order`. Pure: reads the held list only (load it first).
export function workspacePresetsFeatured(root) {
  const r = root || (this && this.state && this.state.root) || null
  const list = r && Array.isArray(r._workspacePresets) ? r._workspacePresets : []
  const rank = (p) => (Number.isFinite(Number(p.order)) ? Number(p.order) : 999)
  return list.filter((p) => p && p.featured === true).sort((a, b) => rank(a) - rank(b))
}

// el.call('workspacePresetByKey', key, root) — one preset of the held list.
export function workspacePresetByKey(key, root) {
  const r = root || (this && this.state && this.state.root) || null
  const list = r && Array.isArray(r._workspacePresets) ? r._workspacePresets : []
  if (typeof key !== 'string' || !key) return null
  return list.find((p) => p && p.key === key) || null
}

// el.call('workspacePresetApplied', root) — the active workspace's
// `settings.preset` ({ key, appliedAt, appliedBy, apps }) or null. A
// list-shaped record (no settings yet) reads as null: the only answer that
// differs is the "Applied" mark, and an apply over an applied preset only adds
// what is missing (the server's contract), so the early answer is safe.
export function workspacePresetApplied(root) {
  const r = root || (this && this.state && this.state.root) || null
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const settings = ws && ws.settings && typeof ws.settings === 'object' ? ws.settings : null
  const preset = settings && settings.preset
  if (!preset || typeof preset !== 'object') return null
  return typeof preset.key === 'string' && preset.key ? preset : null
}

// el.call('workspacePresetCanApply', root) — may this person apply a preset to
// the ACTIVE workspace? The server gates the apply on the workspace role
// owner/admin (superadmin bypasses), and the workspace read carries exactly
// that role (`access.role`, WorkspaceController.callerAccess). Before the read
// lands the org role answers: owner → owner, co-owner/admin/maintainer → admin
// is the server's own cascade (AccessResolver ORG_TO_WORKSPACE_ROLE). A wrong
// early "yes" costs one refused apply, shown as its own line.
export function workspacePresetCanApply(root) {
  const r = root || (this && this.state && this.state.root) || null
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const org = r && r.org && typeof r.org === 'object' ? r.org : null
  if (!ws || !(ws._id || ws.id)) return false
  const access = ws.access && typeof ws.access === 'object' ? ws.access : null
  const role = access && typeof access.role === 'string' ? access.role.trim().toLowerCase() : ''
  if (role) return role === 'owner' || role === 'admin' || role === 'superadmin'
  const orgRole = org && typeof org.role === 'string' ? org.role.trim().toLowerCase() : ''
  return ['owner', 'co-owner', 'admin', 'maintainer'].indexOf(orgRole) !== -1
}

// el.call('workspacePresetText', key, field) — the store's copy for a preset:
// `name`, `line` (the card's one line) or `summary` (the sheet's plain-words
// paragraph). The server owns WHICH presets exist; each surface owns how it
// names them (CONTRACT §2), so a preset this surface has no copy for answers ''
// and the store does not show it. Every registry key has a name and a line;
// the featured three also have a summary. Literal keys, one set per preset, so the
// i18n gates can see every one.
export function workspacePresetText(key, field) {
  const el = this
  const KEYS = {
    'virtual-assistance': {
      name: 'marketplace.preset.virtualAssistance.name',
      line: 'marketplace.preset.virtualAssistance.line',
      summary: 'marketplace.preset.virtualAssistance.summary'
    },
    'real-estate': {
      name: 'marketplace.preset.realEstate.name',
      line: 'marketplace.preset.realEstate.line',
      summary: 'marketplace.preset.realEstate.summary'
    },
    ecommerce: {
      name: 'marketplace.preset.ecommerce.name',
      line: 'marketplace.preset.ecommerce.line',
      summary: 'marketplace.preset.ecommerce.summary'
    },
    // The rest of the registry — the welcome's "More kinds of work" (persona
    // round 3, W3): a name and a line each; no summary, because no store sheet
    // shows them (the store lists the featured presets only).
    hospitality: {
      name: 'marketplace.preset.hospitality.name',
      line: 'marketplace.preset.hospitality.line'
    },
    medical: {
      name: 'marketplace.preset.medical.name',
      line: 'marketplace.preset.medical.line'
    },
    finance: {
      name: 'marketplace.preset.finance.name',
      line: 'marketplace.preset.finance.line'
    },
    education: {
      name: 'marketplace.preset.education.name',
      line: 'marketplace.preset.education.line'
    },
    logistics: {
      name: 'marketplace.preset.logistics.name',
      line: 'marketplace.preset.logistics.line'
    },
    manufacturing: {
      name: 'marketplace.preset.manufacturing.name',
      line: 'marketplace.preset.manufacturing.line'
    },
    media: {
      name: 'marketplace.preset.media.name',
      line: 'marketplace.preset.media.line'
    },
    services: {
      name: 'marketplace.preset.services.name',
      line: 'marketplace.preset.services.line'
    },
    agro: {
      name: 'marketplace.preset.agro.name',
      line: 'marketplace.preset.agro.line'
    },
    aerial: {
      name: 'marketplace.preset.aerial.name',
      line: 'marketplace.preset.aerial.line'
    }
  }
  const set =
    typeof key === 'string' && Object.prototype.hasOwnProperty.call(KEYS, key) ? KEYS[key] : null
  const bundleKey = set && typeof field === 'string' ? set[field] : null
  if (!bundleKey || !el || typeof el.call !== 'function') return ''
  const text = el.call('polyglot', bundleKey)
  return typeof text === 'string' && text && text !== bundleKey ? text : ''
}

// el.call('workspacePresetApply', key, { include, exclude, setup }) — apply a
// preset to the ACTIVE workspace in ONE server call, with a line per app on
// root._presetApply. The plan is the preset's apps minus `exclude` plus
// `include` (the server's own rule); each line starts 'pending' and takes the
// server's answer (installed / skipped / failed — an app this environment does
// not carry fails as `app_not_found` and never blocks the rest). After the
// answer the active workspace record is read again, so `settings.preset` and
// the new apps reach the shell (setActiveWorkspaceRecord, then the shell's
// reactivateWorkspaceModule where it is registered — the store's own install
// refresh). Resolves the result — `{ ok, error, cause? }` on a refusal — never
// rejects.
//
// THE CUSTOM WORKSPACE — `key === 'custom'` (the server's reserved key: the
// wizard's "Something else"). It is no preset: not in the registry, no copy,
// no setup. Its plan is `include` alone, sent as `{ include }`; the server
// installs those apps and ALWAYS records `settings.preset = { key: 'custom',
// … }`, so no surface asks "Your work" again. A server without the key answers
// 404 — then the apps go in through the App Store's own install call
// (`sdk.installWorkspaceApps`, one atomic install, the install button's call)
// and nothing is recorded. The server's answer decides, never a version guess.
export async function workspacePresetApply(key, opts) {
  const el = this
  const root = (el && el.state && el.state.root) || null
  const o = opts && typeof opts === 'object' ? opts : {}
  if (!root || typeof root.update !== 'function' || typeof key !== 'string' || !key) {
    return { ok: false, error: 'invalid' }
  }
  const current = root._presetApply
  if (current && current.status === 'running') return { ok: false, error: 'busy' }
  const ws = root.workspace && typeof root.workspace === 'object' ? root.workspace : null
  const wsId = ws ? String(ws._id || ws.id || '') : ''
  // A refusal: the error line's state, and the caller's answer. `cause` rides
  // along only when it is known (a failed call: 'offline' | 'server').
  const refuse = (error, cause) => {
    const known = cause ? { cause } : {}
    root.update({
      _presetApply: {
        key,
        workspaceId: wsId,
        status: 'error',
        lines: [],
        result: null,
        error,
        ...known
      }
    })
    return { ok: false, error, ...known }
  }
  const sdk =
    (el && el.sdk) ||
    (el && el.context && el.context.methods && el.context.methods.sdk) ||
    (el && typeof el.call === 'function' ? el.call('getSDK') : null) ||
    null
  if (!sdk || sdk.isNullClient === true || typeof sdk.applyWorkspacePreset !== 'function') {
    return refuse('unsupported')
  }
  if (!wsId) return refuse('noWorkspace')
  const custom = key === 'custom'
  let preset = custom
    ? { key, apps: [], suggested: [], setup: { channels: [], folders: [] } }
    : el.call('workspacePresetByKey', key, root)
  if (!preset) {
    await el.call('workspacePresetsLoad')
    preset = el.call('workspacePresetByKey', key, root)
  }
  if (!preset) return refuse('unknownPreset')

  // The plan, in install order: the preset's apps minus `exclude`, then the
  // picked extras. An id is `owner/key`, lower-cased; at most 20 extras (the
  // server's cap). A custom workspace has no preset apps to exclude.
  const norm = (id) =>
    String(id || '')
      .trim()
      .toLowerCase()
  const valid = (id) => /^[a-z0-9][a-z0-9_.-]*\/[a-z0-9][a-z0-9_.-]*$/.test(id)
  const exclude = custom ? [] : (Array.isArray(o.exclude) ? o.exclude : []).map(norm).filter(valid)
  const include = (Array.isArray(o.include) ? o.include : []).map(norm).filter(valid).slice(0, 20)
  const plan = []
  for (const id of (preset.apps || []).map(norm)) {
    if (valid(id) && exclude.indexOf(id) === -1 && plan.indexOf(id) === -1) plan.push(id)
  }
  for (const id of include) if (plan.indexOf(id) === -1) plan.push(id)
  const pending = plan.map((appId) => ({ appId, state: 'pending', error: null }))
  root.update({
    _presetApply: {
      key,
      workspaceId: wsId,
      status: 'running',
      lines: pending,
      result: null,
      error: null
    }
  })

  // A workspace switch while the call runs wipes `_presetApply` (a scoped key)
  // and makes another workspace active: from then on nothing here writes, so
  // the NEW workspace never inherits this apply's lines, its "busy" status or
  // its refresh. The server applied the preset to the workspace it was asked
  // about; the caller still gets that answer.
  const stillHere = () => {
    const now = root.workspace
    return !!now && String(now._id || now.id || '') === wsId
  }

  let res
  try {
    const body = custom ? { include } : { setup: o.setup !== false }
    if (!custom && include.length) body.include = include
    if (!custom && exclude.length) body.exclude = exclude
    res = await sdk.applyWorkspacePreset(wsId, key, body)
  } catch (err) {
    // A refused call installed nothing this read can name: the lines go, the
    // error says what happened. 403 — not an owner/admin. The server's own
    // `unknown_preset` (its body's `error`, on a 404 or a 400) — no such
    // preset on this server. Anything else is a FAILED call, and the sheet
    // offers the retry: the network, a 5xx, a timeout, a server without the
    // apply route yet (a bare 404 — it says nothing about the preset). A
    // failed call names its cause when it is known: 'offline' (the request
    // never reached the server — the SDK's NETWORK_UNREACHABLE, or the
    // browser's own fetch failure) or 'server' (a 5xx or a 408).
    const status = err && Number(err.status)
    if (!(custom && status === 404 && typeof sdk.installWorkspaceApps === 'function')) {
      const body = err && err.cause && typeof err.cause === 'object' ? err.cause : null
      const said = body && typeof body.error === 'string' ? body.error : ''
      const code =
        status === 403 ? 'forbidden' : said === 'unknown_preset' ? 'unknownPreset' : 'failed'
      const offline =
        !status &&
        ((err && err.code === 'NETWORK_UNREACHABLE') ||
          /failed to fetch|networkerror|load failed/i.test(String((err && err.message) || '')))
      const cause =
        code !== 'failed'
          ? ''
          : offline
            ? 'offline'
            : status >= 500 || status === 408
              ? 'server'
              : ''
      if (stillHere()) return refuse(code, cause)
      return cause ? { ok: false, error: code, cause } : { ok: false, error: code }
    }
    // An older server: no `custom` key. The custom workspace's apps go in
    // through the store's own install call — atomic, so every app installs or
    // every app is a failed line with the retry.
    const none = { ok: true, preset: key, installed: [], skipped: [], failed: [] }
    if (!include.length) res = { ...none, setup: null, workspacePreset: null }
    else {
      try {
        const out = await sdk.installWorkspaceApps(wsId, {
          appId: include[0],
          alsoInstall: include.slice(1)
        })
        res = {
          ...none,
          installed: (out && Array.isArray(out.installed) && out.installed) || [],
          skipped: (out && Array.isArray(out.alreadyInstalled) && out.alreadyInstalled) || [],
          setup: null,
          workspacePreset: null
        }
      } catch (installErr) {
        console.warn(
          '[workspacePresets] the custom install failed:',
          (installErr && installErr.message) || installErr
        )
        res = {
          ...none,
          ok: false,
          failed: include.map((appId) => ({ appId, error: 'failed' })),
          setup: null,
          workspacePreset: null
        }
      }
    }
  }
  const result = res && typeof res === 'object' ? res : {}
  if (result.ok === false && result.error) {
    const code = result.error === 'unknown_preset' ? 'unknownPreset' : 'failed'
    return stillHere() ? refuse(code) : { ok: false, error: code }
  }

  // Each planned app takes the server's answer. An app the server installed
  // beyond the plan (a dependency the atomic install brought) gets its own
  // line. `workspacePreset.apps` lists every app present after the apply, so a
  // planned app no list names reads present (skipped) or absent (failed).
  const idOf = (x) =>
    norm(
      typeof x === 'string' ? x : x && (x.appId || (x.owner && x.key ? x.owner + '/' + x.key : ''))
    )
  const installed = (Array.isArray(result.installed) ? result.installed : []).map(idOf)
  const skipped = (Array.isArray(result.skipped) ? result.skipped : []).map(idOf)
  const failed = {}
  for (const f of Array.isArray(result.failed) ? result.failed : []) {
    const id = idOf(f)
    if (id) failed[id] = (f && (f.error || f.code)) || 'failed'
  }
  // When nothing landed (`ok: false`) the server answers the EARLIER record,
  // which may name another preset — its apps say nothing about this plan.
  const wp = result.workspacePreset && typeof result.workspacePreset === 'object'
  const present =
    wp && result.workspacePreset.key === key && Array.isArray(result.workspacePreset.apps)
      ? result.workspacePreset.apps.map(idOf)
      : []
  const lineFor = (appId) => {
    if (installed.indexOf(appId) !== -1) return { appId, state: 'installed', error: null }
    if (skipped.indexOf(appId) !== -1) return { appId, state: 'skipped', error: null }
    if (failed[appId]) return { appId, state: 'failed', error: failed[appId] }
    if (present.indexOf(appId) !== -1) return { appId, state: 'skipped', error: null }
    return { appId, state: 'failed', error: 'failed' }
  }
  const lines = plan.map(lineFor)
  for (const id of installed) {
    if (id && plan.indexOf(id) === -1) lines.push({ appId: id, state: 'installed', error: null })
  }
  const setupResult = result.setup && typeof result.setup === 'object' ? result.setup : null
  const plain = {
    ok: result.ok !== false,
    preset: result.preset || key,
    installed,
    skipped,
    failed: Object.keys(failed).map((appId) => ({ appId, error: failed[appId] })),
    setup: setupResult ? JSON.parse(JSON.stringify(setupResult)) : null,
    workspacePreset: wp ? JSON.parse(JSON.stringify(result.workspacePreset)) : null
  }
  if (!stillHere()) return { ...plain, lines, switched: true }
  root.update({
    _presetApply: { key, workspaceId: wsId, status: 'running', lines, result: plain, error: null }
  })

  // THE REFRESH — the active workspace record, read again: `settings.preset`
  // shows and the shell loads the new apps (navbar tabs, pages, widgets), so
  // "Open <app>" finds its route. The shell loader is waited for at most 20 s:
  // it keeps running past that, but the apply is done and says so — a slow
  // loader must never leave the status "running" (that would read "busy" to
  // the next apply).
  try {
    const full = await el.call('fetchFullWorkspaceRecord', sdk, wsId)
    if (full && stillHere()) {
      el.call('setActiveWorkspaceRecord', root, full, { sdk, reason: 'workspacePresetApply' })
      const fns = (el.context && el.context.functions) || {}
      if (typeof fns.reactivateWorkspaceModule === 'function') {
        const settings = (full.settings && typeof full.settings === 'object' && full.settings) || {}
        // QUIET (R2c-m2): this reload follows the person's own apply — the
        // welcome sends it in the background while the person goes on, and a
        // "Retrying workspace loading" strip then sat over the welcome's
        // Continue (elena-r2c, sam-r2). A failure still shows.
        const loading = Promise.resolve(
          el.call(
            'reactivateWorkspaceModule',
            null,
            { workspaceApps: Array.isArray(settings.workspaceApps) ? settings.workspaceApps : [] },
            { quiet: true }
          )
        )
        let timer = null
        const bound = new Promise((resolve) => {
          timer = setTimeout(resolve, 20000)
        })
        try {
          await Promise.race([loading, bound])
        } finally {
          clearTimeout(timer)
        }
      }
    }
  } catch (err) {
    console.warn('[workspacePresets] the workspace refresh failed:', (err && err.message) || err)
  }
  if (!stillHere()) return { ...plain, lines, switched: true }
  root.update({
    _presetApply: { key, workspaceId: wsId, status: 'done', lines, result: plain, error: null }
  })
  return { ...plain, lines }
}
